import { Link } from "react-router-dom";
import { ArrowRight, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import { getSupabase } from "@/integrations/supabase/lazy";
import heroDesktop from "@/assets/hero-stemke-werk.webp.asset.json";
import heroMobile from "@/assets/hero-stemke-werk-mobile.webp.asset.json";


export function HeroSection() {
  const [memberCount, setMemberCount] = useState<number | null>(null);

  useEffect(() => {
    // Nicht kritisch: erst nach dem ersten Rendern laden
    const run = () => {
      getSupabase().then((supabase) =>
        supabase.rpc("get_active_member_count").then(({ data }) => {
          if (typeof data === "number") setMemberCount(data);
        })
      );
    };
    const w = window as Window & { requestIdleCallback?: (cb: () => void) => number };
    const id = w.requestIdleCallback ? w.requestIdleCallback(run) : window.setTimeout(run, 1200);
    return () => {
      if (!w.requestIdleCallback) window.clearTimeout(id);
    };
  }, []);

  return (
    <section className="relative min-h-[100svh] flex items-center justify-center overflow-hidden">
      {/* Background */}
      <img
        src={heroDesktop.url}
        srcSet={`${heroMobile.url} 900w, ${heroDesktop.url} 1600w`}

        sizes="100vw"
        alt="Altes Stemke Werk in Döbeln – Veranstaltungsort von Kultmans e.V."
        fetchPriority="high"
        decoding="async"
        width={1600}
        height={960}
        className="absolute inset-0 h-full w-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/80 to-background" />
      
      {/* Content */}
      <div className="container relative z-10 mx-auto px-4 pt-20">
        <div className="max-w-4xl mx-auto text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs md:text-sm font-medium mb-6 md:mb-8 animate-fade-in">
            <Users size={14} className="shrink-0" />
            <span>Ehrenamtlicher Kultur- und Begegnungsort in Döbeln</span>
          </div>

          {/* Title */}
          <h1 
            className="font-display text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-bold text-foreground mb-4 md:mb-6 leading-tight"
          >
            Kultmans e.V. –{" "}
            <span className="text-gradient">Kulturmanufaktur im Alten Stemke</span>
          </h1>

          {/* Direct Answer / AEO Subtitle */}
          <p 
            className="text-base sm:text-lg md:text-2xl text-muted-foreground max-w-3xl mx-auto mb-8 md:mb-10 animate-fade-in"
            style={{ animationDelay: "0.2s" }}
          >
            Kultmans e.V. organisiert Musikveranstaltungen, Sportkurse, KI-Workshops und Netzwerktreffen 
            im Alten Stemke Werk in Döbeln, um Bürger unterschiedlicher Altersgruppen zusammenzubringen – 
            100 % ehrenamtlich.
          </p>

          {/* CTAs */}
          <div 
            className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in"
            style={{ animationDelay: "0.3s" }}
          >
            <Button variant="hero" size="xl" asChild>
              <Link to="/mitglied-werden">
                Mitglied werden
                <ArrowRight size={20} />
              </Link>
            </Button>
            <Button variant="heroOutline" size="xl" asChild>
              <Link to="/verein">
                Mehr erfahren
              </Link>
            </Button>
          </div>

          {/* Stats */}
          <div 
            className="mt-12 md:mt-20 grid grid-cols-3 gap-4 md:gap-8 max-w-xl mx-auto animate-fade-in"
            style={{ animationDelay: "0.4s" }}
          >
            <div>
              <p className="font-display text-2xl sm:text-3xl md:text-4xl font-bold text-primary">1000m²</p>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1">Veranstaltungsfläche</p>
            </div>
            <div>
              <p className="font-display text-2xl sm:text-3xl md:text-4xl font-bold text-primary">{memberCount ?? "40+"}</p>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1">Aktive Mitglieder</p>
            </div>
            <div>
              <p className="font-display text-2xl sm:text-3xl md:text-4xl font-bold text-primary">2023</p>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1">Gegründet</p>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 rounded-full border-2 border-muted-foreground/30 flex items-start justify-center p-2">
          <div className="w-1 h-2 rounded-full bg-primary" />
        </div>
      </div>
    </section>
  );
}
